import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';

const FlashUpgradesPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeUpgrades, setActiveUpgrades] = useState([]);
  const [config, setConfig] = useState({
    default_duration_days: 14,
    send_enabled_email: true,
    send_warning_email: true,
    warning_days_before_expiry: 3,
    send_expired_email: true
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const getToken = useCallback(() => {
    return localStorage.getItem('token');
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      const [configRes, upgradesRes] = await Promise.all([
        axios.get(`${API}/super-admin/flash-upgrades/config`, { headers }),
        axios.get(`${API}/super-admin/flash-upgrades`, { headers })
      ]);

      if (configRes.data) {
        setConfig(configRes.data);
      }

      if (upgradesRes.data) {
        setActiveUpgrades(upgradesRes.data.organisations || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setErrorMessage('Failed to load flash upgrade data');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveConfig = async () => {
    setSaving(true);
    setSuccessMessage('');
    setErrorMessage('');
    try {
      const token = getToken();
      await axios.put(`${API}/super-admin/flash-upgrades/config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccessMessage('Configuration saved successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving config:', error);
      setErrorMessage('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDisableFlashUpgrade = async (orgId, orgName) => {
    if (!window.confirm(`Are you sure you want to disable the flash upgrade for ${orgName}? This will immediately revert them to their original tier and may deactivate sites/users.`)) {
      return;
    }

    try {
      const token = getToken();
      await axios.post(`${API}/super-admin/flash-upgrades/disable`, 
        { organisation_id: orgId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage(`Flash upgrade disabled for ${orgName}`);
      fetchData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error disabling flash upgrade:', error);
      setErrorMessage('Failed to disable flash upgrade');
    }
  };

  const handleExtendFlashUpgrade = async (orgId, orgName) => {
    const days = window.prompt(`Enter number of days to extend for ${orgName}:`, config.default_duration_days);
    if (!days || isNaN(parseInt(days))) return;

    try {
      const token = getToken();
      await axios.post(`${API}/super-admin/flash-upgrades/extend`, 
        { organisation_id: orgId, duration_days: parseInt(days) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage(`Flash upgrade extended by ${days} days for ${orgName}`);
      fetchData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error extending flash upgrade:', error);
      setErrorMessage('Failed to extend flash upgrade');
    }
  };

  if (loading) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
          <span className="ml-3 text-gray-600 text-lg">Loading flash upgrades...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white mb-8 shadow-xl">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h1 className="text-3xl font-bold">Flash Upgrades</h1>
            <p className="text-purple-200 text-sm">Grant temporary Pro tier access to organisations for trials, demos, or support</p>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-6 bg-purple-50 border-2 border-purple-300 rounded-lg p-4 shadow-sm">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-purple-600 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-bold text-sm text-purple-900 mb-1">Success!</p>
              <p className="text-purple-800 text-sm">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="mb-6 bg-red-50 border-2 border-red-300 rounded-lg p-4 shadow-sm">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-bold text-sm text-red-900 mb-1">Error</p>
              <p className="text-red-800 text-sm">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Section */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-purple-100 to-purple-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Configuration</h2>
                <p className="text-sm text-gray-600">Default settings for flash upgrades</p>
              </div>
            </div>
            <div className="text-sm text-gray-500 flex items-center space-x-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Enable flash upgrades from the Organisations page</span>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Default Duration */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Default Duration (days)
              </label>
              <input
                type="number"
                value={config.default_duration_days}
                onChange={(e) => setConfig({ ...config, default_duration_days: parseInt(e.target.value) || 14 })}
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors"
                min="1"
                max="90"
              />
            </div>

            {/* Warning Days */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Warning Days Before Expiry
              </label>
              <input
                type="number"
                value={config.warning_days_before_expiry}
                onChange={(e) => setConfig({ ...config, warning_days_before_expiry: parseInt(e.target.value) || 3 })}
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors"
                min="0"
                max="14"
              />
            </div>

            {/* Email Toggles */}
            <div className="md:col-span-2 lg:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email Notifications
              </label>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center space-x-2 px-4 py-2.5 bg-gray-50 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors border border-gray-200">
                  <input
                    type="checkbox"
                    checked={config.send_enabled_email}
                    onChange={(e) => setConfig({ ...config, send_enabled_email: e.target.checked })}
                    className="h-4 w-4 text-purple-500 rounded focus:ring-purple-500 border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Enabled</span>
                </label>

                <label className="flex items-center space-x-2 px-4 py-2.5 bg-gray-50 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors border border-gray-200">
                  <input
                    type="checkbox"
                    checked={config.send_warning_email}
                    onChange={(e) => setConfig({ ...config, send_warning_email: e.target.checked })}
                    className="h-4 w-4 text-purple-500 rounded focus:ring-purple-500 border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Warning</span>
                </label>

                <label className="flex items-center space-x-2 px-4 py-2.5 bg-gray-50 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors border border-gray-200">
                  <input
                    type="checkbox"
                    checked={config.send_expired_email}
                    onChange={(e) => setConfig({ ...config, send_expired_email: e.target.checked })}
                    className="h-4 w-4 text-purple-500 rounded focus:ring-purple-500 border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Expired</span>
                </label>

                <button
                  onClick={handleSaveConfig}
                  disabled={saving}
                  className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-purple-500 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-purple-600 transition-all disabled:opacity-50 shadow-md hover:shadow-lg ml-auto"
                >
                  {saving ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Flash Upgrades Section */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-100 to-purple-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Active Flash Upgrades</h2>
                <p className="text-sm text-gray-600">Organisations currently with temporary Pro access</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="px-3 py-1 bg-purple-500 text-white text-sm font-bold rounded-full">
                {activeUpgrades.length}
              </span>
              <button
                onClick={fetchData}
                className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                title="Refresh"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {activeUpgrades.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <p className="text-gray-500 font-medium">No active flash upgrades</p>
              <p className="text-gray-400 text-sm mt-1">Enable flash upgrades from the Organisations page</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {activeUpgrades.map(org => (
                <div
                  key={org.organisation_id}
                  className="border-2 border-gray-200 rounded-xl p-5 hover:border-purple-300 transition-colors bg-gray-50"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-bold text-gray-900 truncate">{org.name}</h3>
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full flex-shrink-0">
                          Pro Access
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5 truncate">{org.organisation_id}</p>
                      
                      <div className="flex flex-wrap items-center gap-3 mt-3">
                        <span className="flex items-center space-x-1.5 text-sm text-gray-600 bg-white px-3 py-1.5 rounded-lg border border-gray-200">
                          <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-medium">{org.days_remaining} days left</span>
                        </span>
                        <span className="text-xs text-gray-400">
                          Original: {org.flash_upgrade_original_tier || 'Starter'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                      <button
                        onClick={() => handleExtendFlashUpgrade(org.organisation_id, org.name)}
                        className="px-3 py-1.5 text-sm bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors font-medium border border-purple-200"
                        title="Extend"
                      >
                        Extend
                      </button>
                      <button
                        onClick={() => handleDisableFlashUpgrade(org.organisation_id, org.name)}
                        className="px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-medium border border-red-200"
                        title="Disable"
                      >
                        Disable
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlashUpgradesPage;
